import { faTimes, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Divider, Modal, notification } from 'antd';
import { useFormik } from 'formik';
import React, { useState } from 'react';

import colors from '../../../foundation/assets/colors';
import LogoDark from '../../../foundation/assets/svgs/LogoDark';
import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Input from '../../../foundation/components/input/Input';
import Password from '../../../foundation/components/password/Password';
import { addItemToStorage } from '../../../foundation/utils/storageHandler';
import { useAppDispatch } from '../../../store/hooks';
import { globalDataApi } from '../../api/globalDataApi';
import { login } from '../redux/async_thunks';
import { setRequiresPasswordChange } from '../redux/slice';
import { setUsername } from '../redux/slice';
import { REGISTRATION_MAILTO_HREF } from '../registrationConstants';
import ImprovedSignupModal from '../signup/ImprovedSignupModal';
import { validatePasswordLocally } from '../utils/passwordValidation';
import ForgotPasswordModal from './ForgotPasswordModal';
import { LoginFormValidationSchema } from './form_validation';

type LoginProps = {
  /** Callback function to close the login modal */
  onClose: () => void;
  /** Called after a successful login, before the modal closes */
  onLoginSuccess?: () => void;
};

/**
 * Login Component
 *
 * Handles user authentication via username and password.
 * Includes a link for new users to request registration via email.
 */
const Login = ({ onClose, onLoginSuccess }: LoginProps) => {
  const dispatch = useAppDispatch();

  // State to track loading status during authentication
  const [isLoading, setIsLoading] = useState(false);

  // State to track ImprovedSignupModal visibility
  const [isSignupModalVisible, setIsSignupModalVisible] = useState(false);

  // State to track ForgotPasswordModal visibility
  const [isForgotPasswordModalVisible, setIsForgotPasswordModalVisible] =
    useState(false);

  const handleModalClose = () => {
    onClose();
  };

  const modalProps = {
    open: true,
    closable: false,
    footer: null,
    title: null,
    maskClosable: false,
    onCancel: () => handleModalClose(),
    width: 430,
    centered: true,
    zIndex: 3000, // Ensure login modal is always on top
  };

  /**
   * Handles the login form submission.
   * @param values Form values containing username and password.
   */
  const handleFormSubmit = async (values: any) => {
    try {
      if (isLoading) {
        return;
      }

      setIsLoading(true);

      const data = {
        username: values.username,
        password: values.password,
      };

      // Attempt to login using async thunk
      await dispatch(login(data)).unwrap();

      // Store the username as the minimal auth marker.
      dispatch(setUsername(values.username));
      // Prefetch the full UserProfile into the RTK Query cache.
      // Equivalent to the tutorial's
      //   store.dispatch(apiSlice.endpoints.getUsers.initiate())
      await dispatch(
        globalDataApi.endpoints.getUserDetails.initiate({
          username: values.username,
        }),
      ).unwrap();
      // Only save username to storage for session restore.
      // Tokens are now handled via HttpOnly cookies.
      addItemToStorage('_u', JSON.stringify(values.username));

      // Validate the password against the requirements (frontend check).
      // On every login the flag is explicitly set so that:
      //   Weak password   → sessionStorage._rpc = '1' → modal shows
      //   Strong password → sessionStorage._rpc removed → no modal
      // This ensures users are not re-prompted after changing their password.
      const validation = validatePasswordLocally(values.password);
      if (!validation.valid) {
        dispatch(setRequiresPasswordChange(true));
      } else {
        dispatch(setRequiresPasswordChange(false));
      }

      setIsLoading(false);

      // Close modal on success
      onLoginSuccess?.();
      onClose();
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      // Show a clear message for wrong credentials and keep the modal open
      notification.error({
        message: 'Login failed',
        description: 'Invalid username or password. Please try again.',
      });
    }
  };

  /**
   * Formik setup for form management and validation.
   */
  const formik = useFormik({
    initialValues: {
      username: undefined,
      password: undefined,
    },
    onSubmit: (values) => {
      handleFormSubmit(values);
    },
    validationSchema: LoginFormValidationSchema,
  });

  /**
   * Handles clicking on the registration link.
   * Opens the ImprovedSignupModal.
   */
  const handleSignupClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsSignupModalVisible(true);
  };

  const onSignupModalClose = () => {
    setIsSignupModalVisible(false);
  };

  const handleForgotPasswordClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsForgotPasswordModalVisible(true);
  };

  const onForgotPasswordModalClose = () => {
    setIsForgotPasswordModalVisible(false);
  };

  return (
    <Modal
      {...modalProps}
      className="l-login"
      wrapClassName="l-login__modal-wrapper"
      rootClassName="l-login__modal-root"
    >
      {isLoading && <FullPageLoader />}
      <div className="l-login__header">
        <div className="l-login__header-title">
          <div>
            <LogoDark />
          </div>
          <div
            className="l-login__header-close-icon"
            onClick={handleModalClose}
          >
            <FontAwesomeIcon icon={faTimes} color={colors.primary} />
          </div>
        </div>
        <div>Please enter your details to login</div>
      </div>
      <div className="l-login__body">
        <form onSubmit={formik.handleSubmit}>
          <div className="l-login__form-fields">
            <div className="l-login__field-wrapper">
              <Input
                placeholder="Username"
                name="username"
                id="username"
                value={formik.values.username}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.username ? formik.errors.username : undefined
                }
              />
            </div>
            <div className="l-login__field-wrapper">
              <Password
                placeholder="Password"
                name="password"
                id="password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.touched.password ? formik.errors.password : undefined
                }
              />
            </div>
          </div>
          <div className="l-login__form-actions">
            <Button
              className="l-login__form-submit-btn"
              icon={<FontAwesomeIcon icon={faUser} color={colors.primary} />}
              type="primary"
              htmlType="submit"
            >
              Log in
            </Button>
            <span
              className="l-login__form-forgot-btn"
              onClick={handleForgotPasswordClick}
            >
              Forgot password?
            </span>
          </div>
        </form>
      </div>
      <Divider />
      <div className="l-login__footer">
        <span>
          {`Don't have an account?`}{' '}
          <a
            href={REGISTRATION_MAILTO_HREF}
            onClick={handleSignupClick}
            className="l-signup__mailto"
          >
            Apply for new account here
          </a>
        </span>
      </div>
      {isSignupModalVisible && (
        <ImprovedSignupModal onClose={onSignupModalClose} />
      )}
      {isForgotPasswordModalVisible && (
        <ForgotPasswordModal onClose={onForgotPasswordModalClose} />
      )}
    </Modal>
  );
};

export default Login;
